import { useState, useLayoutEffect, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiUrl } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { StepIndicator } from "@/components/measurement/StepIndicator";
import { HandoverPhotoStep } from "./HandoverPhotoStep";
import {
  HandoverProjectMetaStep,
  type HandoverProjectMeta,
} from "./HandoverProjectMetaStep";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { Label } from "@/components/ui/label";
import { patchDealInstallationDate, moveDealStage, todayYmdLocal } from "@/lib/crm-deal-stage";
import { postDealNoteWithOutbox } from "@/lib/post-deal-note-with-outbox";
import { enqueueCrmSyncOp } from "@/lib/crm-sync-outbox";
import { listImageUploadOutboxItems } from "@/lib/image-upload-outbox";
import { notify } from "@/lib/notify";
import { isInstallationDateCaptureStageRow } from "../../../functions/lib/deal-installation-date";

const STEPS = {
  DEAL_SELECT: 1,
  PHOTOS: 2,
  PROJECT_META: 3,
  NOTE: 4,
  SUMMARY: 5,
};

type HandoverStep = (typeof STEPS)[keyof typeof STEPS];

const NO_STAGE_VALUE = "__no_stage_change__";

const EMPTY_META: HandoverProjectMeta = {
  brand: "",
  folderType: "",
  model: "",
  panelStructure: "",
  color: "",
  prelisType: "",
};

interface HandoverWizardProps {
  dealId: string | null;
  /** CRM FAB: aktuális subpipeline — deal lista csak ide, „Kapu nálunk…” + Egyéb csoportokkal */
  scopedPipelineId?: string | null;
  onDealSelect?: (dealId: string) => void;
}

function stageDisplayNameLower(s: Record<string, unknown>): string {
  return String(s.name ?? s.name_hu ?? s.name_sk ?? s.name_en ?? "").toLowerCase();
}

function metaFromFolder(folder: Record<string, unknown> | undefined): HandoverProjectMeta {
  if (!folder) return { ...EMPTY_META };
  return {
    brand: String(folder.brand ?? "").trim(),
    folderType: String(folder.folder_type ?? folder.folderType ?? "").trim(),
    model: String(folder.model ?? "").trim(),
    panelStructure: String(folder.panel_structure ?? folder.panelStructure ?? "").trim(),
    color: String(folder.color ?? "").trim(),
    prelisType: String(folder.prelis_type ?? folder.prelisType ?? "").trim(),
  };
}

export function HandoverWizard({ dealId: initialDealId, scopedPipelineId = null, onDealSelect }: HandoverWizardProps) {
  const [dealId, setDealId] = useState<string | null>(initialDealId);
  const [currentStep, setCurrentStep] = useState<HandoverStep>(() =>
    initialDealId ? STEPS.PHOTOS : STEPS.DEAL_SELECT
  );
  const [dealSelectOpen, setDealSelectOpen] = useState(false);
  const [dealSearchValue, setDealSearchValue] = useState("");
  const [noteText, setNoteText] = useState("");
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [selectedStageChoice, setSelectedStageChoice] = useState<string>(NO_STAGE_VALUE);
  const [installationDate, setInstallationDate] = useState(() => todayYmdLocal());
  const [finishing, setFinishing] = useState(false);
  const [photoUploadBusy, setPhotoUploadBusy] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>();
  const [projectMeta, setProjectMeta] = useState<HandoverProjectMeta>({ ...EMPTY_META });
  const [metaHydratedForFolder, setMetaHydratedForFolder] = useState<string | null>(null);

  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const isMobile = useIsMobile() === true;

  const onUploadBusyChange = useCallback((busy: boolean) => {
    setPhotoUploadBusy(busy);
  }, []);
  const onFolderIdChange = useCallback((id: string | undefined) => {
    setActiveFolderId(id);
  }, []);

  useEffect(() => {
    setDealId(initialDealId);
    setCurrentStep(initialDealId ? STEPS.PHOTOS : STEPS.DEAL_SELECT);
    setProjectMeta({ ...EMPTY_META });
    setMetaHydratedForFolder(null);
    setActiveFolderId(undefined);
  }, [initialDealId]);

  const board = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/crm/board"), { credentials: "include" });
      if (!r.ok) throw new Error(`Board ${r.status}`);
      return r.json();
    },
  });

  const handoverDeals = useQuery({
    queryKey: ["handover-deals", scopedPipelineId ?? ""],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (scopedPipelineId) params.set("pipelineId", scopedPipelineId);
      const qs = params.toString();
      const r = await fetch(apiUrl(`/api/crm/deals/handover${qs ? `?${qs}` : ""}`), { credentials: "include" });
      if (!r.ok) throw new Error(`Handover deals ${r.status}`);
      const j = await r.json();
      const groups = (j.groups ?? [{ label: "Egyéb", deals: j.deals ?? [] }]) as { label: string; deals: any[] }[];
      return groups;
    },
  });

  const deal = useQuery({
    queryKey: ["deal", dealId],
    enabled: !!dealId,
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
      if (!r.ok) throw new Error(`Deal ${r.status}`);
      return r.json();
    },
  });

  const dealTitle = String(deal.data?.title ?? (deal.data as any)?.name ?? "").trim() || dealId || "—";

  const foldersQuery = useQuery({
    queryKey: ["image-folders", { dealId: dealId ?? "" }],
    queryFn: async () => {
      if (!dealId) return [];
      const r = await fetch(apiUrl(`/api/images/folders?dealId=${dealId}`), { credentials: "include" });
      if (!r.ok) return [];
      const j = await r.json();
      return j.folders as any[];
    },
    enabled: !!dealId,
  });

  const folderFromList = foldersQuery.data?.[0] as Record<string, unknown> | undefined;
  const folderId = activeFolderId ?? (folderFromList?.id as string | undefined);

  useEffect(() => {
    if (!folderId || !foldersQuery.data?.length) return;
    if (metaHydratedForFolder === folderId) return;
    const folder =
      (foldersQuery.data as any[]).find((f) => f.id === folderId) || foldersQuery.data[0];
    setProjectMeta(metaFromFolder(folder));
    setMetaHydratedForFolder(folderId);
  }, [folderId, foldersQuery.data, metaHydratedForFolder]);

  const munkainkCountQuery = useQuery({
    queryKey: ["image-folder-photos", folderId, "munkaink", "handover-summary"],
    queryFn: async () => {
      if (!folderId) return [];
      const params = new URLSearchParams({ subfolderType: "munkaink" });
      const r = await fetch(apiUrl(`/api/images/folders/${folderId}/photos?${params}`), {
        credentials: "include",
      });
      if (!r.ok) return [];
      const j = await r.json();
      return j.photos as any[];
    },
    enabled: !!folderId,
  });
  const munkainkPhotoCount = (munkainkCountQuery.data || []).length;

  const matchDealSearch = (dDeal: any) => {
    if (!dealSearchValue.trim()) return true;
    const searchLower = dealSearchValue.toLowerCase().trim();
    const title = String(dDeal.title ?? dDeal.name ?? "").toLowerCase();
    const id = String(dDeal.id ?? "").toLowerCase();
    return (
      title.includes(searchLower) ||
      id.includes(searchLower) ||
      title.split(/\s+/).some((word: string) => word.includes(searchLower))
    );
  };

  const filteredGroups = useMemo(() => {
    const groups = handoverDeals.data ?? [];
    return groups
      .map((g) => ({
        label: g.label,
        heading: g.label.toLowerCase().includes("kapu nálunk")
          ? t("handover.dealGroupPriority")
          : t("handover.dealGroupOther"),
        deals: g.deals.filter(matchDealSearch),
      }))
      .filter((g) => g.deals.length > 0);
  }, [handoverDeals.data, dealSearchValue, t]);

  const dealPipelineId = useMemo(() => {
    const currentStageId = String(deal.data?.stageId ?? (deal.data as any)?.stage_id ?? "").trim();
    const currentStage = (board.data?.stages as any[] | undefined)?.find(
      (s) => String(s.id ?? s.stage_id ?? "") === currentStageId,
    );
    const fromCurrentStage = currentStage
      ? String(currentStage.pipelineId ?? currentStage.pipeline_id ?? "").trim()
      : "";
    const fromDeal = String(deal.data?.pipelineId ?? (deal.data as any)?.pipeline_id ?? "").trim();
    return fromCurrentStage || fromDeal || String(scopedPipelineId ?? "").trim();
  }, [deal.data, board.data?.stages, scopedPipelineId]);

  const stagesInDealSubpipeline = useMemo(() => {
    if (!dealPipelineId || !board.data?.stages) return [];
    const stages = (board.data.stages as any[]).filter(
      (s) => String(s.pipelineId ?? s.pipeline_id ?? "") === dealPipelineId
    );
    return [...stages].sort((a, b) => {
      const ao = Number(a.order ?? a.order_index ?? a.orderIndex ?? 0);
      const bo = Number(b.order ?? b.order_index ?? b.orderIndex ?? 0);
      return ao - bo;
    });
  }, [board.data?.stages, dealPipelineId]);

  const stepLabels = [
    t("handover.stepDeal"),
    t("handover.stepPhotos"),
    t("handover.stepProjectMeta"),
    t("handover.stepNote"),
    t("handover.stepSummary"),
  ];

  const handleDealSelect = (id: string) => {
    setDealId(id);
    setDealSelectOpen(false);
    onDealSelect?.(id);
    setProjectMeta({ ...EMPTY_META });
    setMetaHydratedForFolder(null);
    setActiveFolderId(undefined);
    setCurrentStep(STEPS.PHOTOS);
  };

  const goNext = () => {
    if (currentStep < STEPS.SUMMARY) setCurrentStep((s) => (s + 1) as HandoverStep);
  };

  const minStep = initialDealId ? STEPS.PHOTOS : STEPS.DEAL_SELECT;
  const goPrev = () => {
    if (currentStep > minStep) setCurrentStep((s) => (s - 1) as HandoverStep);
  };

  const pickDefaultTargetStageId = () => {
    const list = stagesInDealSubpipeline;
    if (!list.length) return NO_STAGE_VALUE;
    const captureStages = list.filter((s) =>
      isInstallationDateCaptureStageRow(s as Record<string, unknown>),
    );
    const won =
      captureStages.find((s) => {
        const n = stageDisplayNameLower(s);
        return n.includes("closed won") || n.includes("deal won");
      }) ?? captureStages[0];
    const sid = won ? String(won.id ?? won.stage_id ?? "") : "";
    return sid || NO_STAGE_VALUE;
  };

  const openFinishDialog = async () => {
    if (photoUploadBusy) {
      notify.error(t("handover.blockedUploadBusy"), t("handover.blockedUploadBusyDesc"));
      return;
    }
    if (munkainkPhotoCount === 0) {
      notify.error(t("handover.photosHintMin"), "");
      return;
    }
    if (dealId || folderId) {
      try {
        const pending = await listImageUploadOutboxItems();
        const relevant = pending.filter(
          (item) =>
            (folderId && item.context.folderId === folderId) ||
            (dealId && item.context.dealId === dealId),
        );
        if (relevant.length > 0) {
          notify.error(t("handover.blockedPendingUploads"), t("handover.blockedPendingUploadsDesc"));
          return;
        }
      } catch {
        /* ignore outbox read errors */
      }
    }
    setSelectedStageChoice(pickDefaultTargetStageId());
    setInstallationDate(todayYmdLocal());
    setStageDialogOpen(true);
  };

  const confirmFinish = async () => {
    if (!dealId) return;
    if (photoUploadBusy) {
      notify.error(t("handover.blockedUploadBusy"), t("handover.blockedUploadBusyDesc"));
      return;
    }
    if (munkainkPhotoCount === 0 || !folderId) {
      notify.error(t("handover.photosHintMin"), "");
      return;
    }

    setFinishing(true);
    try {
      try {
        const pending = await listImageUploadOutboxItems();
        const relevant = pending.filter(
          (item) =>
            item.context.folderId === folderId ||
            (dealId && item.context.dealId === dealId),
        );
        if (relevant.length > 0) {
          notify.error(t("handover.blockedPendingUploads"), t("handover.blockedPendingUploadsDesc"));
          return;
        }
      } catch {
        /* ignore */
      }

      const folderRes = await fetch(apiUrl(`/api/images/folders/${folderId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          brand: projectMeta.brand || null,
          folderType: projectMeta.folderType || null,
          model: projectMeta.model || null,
          panelStructure: projectMeta.panelStructure || null,
          color: projectMeta.color || null,
          prelisType: projectMeta.prelisType || null,
          installDate: installationDate,
          category: "realizacio",
          handoverAt: new Date().toISOString(),
        }),
      });
      if (!folderRes.ok) {
        notify.error(t("handover.folderMetaSaveFailed"), "");
        return;
      }

      const trimmed = noteText.trim();
      if (trimmed) {
        const body = `${t("handover.notePrefix")}${trimmed}`;
        const ok = await postDealNoteWithOutbox(dealId, body);
        if (!ok) {
          notify.info(t("uploadOutbox.noteQueued"), t("uploadOutbox.noteQueuedDesc"));
        }
      }

      await patchDealInstallationDate(dealId, installationDate);

      if (selectedStageChoice !== NO_STAGE_VALUE) {
        let stageOk = false;
        try {
          await moveDealStage(dealId, {
            stageId: selectedStageChoice,
            keepSubpipeline: true,
            pipelineId: dealPipelineId || undefined,
            installationDate,
          });
          stageOk = true;
        } catch {
          stageOk = false;
        }
        if (!stageOk) {
          await enqueueCrmSyncOp("deal_stage", dealId, {
            stageId: selectedStageChoice,
            keepSubpipeline: true,
            ...(dealPipelineId ? { pipelineId: dealPipelineId } : {}),
            installationDate,
          });
          notify.info(t("uploadOutbox.patchQueued"), t("uploadOutbox.patchQueuedDesc"));
        }
      }

      try {
        await fetch(apiUrl(`/api/crm/deals/${dealId}/marketing-webhook`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        });
      } catch {
        /* non-blocking */
      }

      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      qc.invalidateQueries({ queryKey: queryKeys.crmBoard });
      qc.invalidateQueries({ queryKey: ["image-folders", { dealId }] });
      setStageDialogOpen(false);
      navigate("/crm");
    } finally {
      setFinishing(false);
    }
  };

  const stageLabel = (s: any) => String(s.name ?? s.name_hu ?? s.id ?? "");

  const stepIndicator = (
    <StepIndicator currentStep={currentStep} totalSteps={5} stepLabels={stepLabels} />
  );

  const [mobileHeaderEl, setMobileHeaderEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (isMobile) setMobileHeaderEl(document.getElementById("handover-step-header"));
  }, [isMobile]);

  if (isMobile && mobileHeaderEl) {
    createPortal(stepIndicator, mobileHeaderEl);
  }

  const nextDisabled =
    (currentStep === STEPS.DEAL_SELECT && !dealId) ||
    (currentStep === STEPS.PHOTOS && (munkainkPhotoCount === 0 || photoUploadBusy));

  return (
    <div className="h-full flex flex-col">
      {!isMobile && stepIndicator}

      <div className={cn("flex-1 overflow-y-auto", isMobile && "pb-28")}>
        {currentStep === STEPS.DEAL_SELECT && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("handover.selectDealTitle")}</CardTitle>
                <CardDescription>{t("handover.selectDealDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <Popover open={dealSelectOpen} onOpenChange={setDealSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      {dealId && dealTitle !== "—" ? dealTitle : t("handover.selectPlaceholder")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    side="bottom"
                    avoidCollisions={false}
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t("handover.searchDeals")}
                        value={dealSearchValue}
                        onValueChange={setDealSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        {filteredGroups.map((group) => (
                          <CommandGroup key={group.label} heading={group.heading}>
                            {group.deals.map((d: any) => (
                              <CommandItem
                                key={d.id}
                                value={`${group.label}-${d.id}-${d.title || d.id}`}
                                onSelect={() => handleDealSelect(d.id)}
                              >
                                <Check className={cn("mr-2 h-4 w-4", dealId === d.id ? "opacity-100" : "opacity-0")} />
                                <span className="flex-1 truncate">{d.title || d.name || d.id}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => handoverDeals.refetch()}
                  disabled={handoverDeals.isFetching}
                >
                  {handoverDeals.isFetching ? t("common.loading") : t("common.refresh")}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {currentStep === STEPS.PHOTOS && dealId && (
          <HandoverPhotoStep
            dealId={dealId}
            onPhotosChanged={() => void munkainkCountQuery.refetch()}
            onUploadBusyChange={onUploadBusyChange}
            onFolderIdChange={onFolderIdChange}
          />
        )}

        {currentStep === STEPS.PROJECT_META && (
          <HandoverProjectMetaStep value={projectMeta} onChange={setProjectMeta} />
        )}

        {currentStep === STEPS.NOTE && (
          <div className="p-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("handover.noteTitle")}</CardTitle>
                <CardDescription>{t("handover.noteDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t("handover.notePlaceholder")}
                  className="w-full min-h-[140px] px-3 py-2 rounded-lg border bg-background text-sm"
                  rows={5}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {currentStep === STEPS.SUMMARY && dealId && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("handover.summaryTitle")}</CardTitle>
                <CardDescription>{t("handover.summaryDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryDeal")}</span>{" "}
                  <span className="font-medium">{dealTitle}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryPhotos")}</span>{" "}
                  <span className="font-medium">{munkainkPhotoCount}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryBrand")}</span>{" "}
                  <span className="font-medium">{projectMeta.brand || "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryType")}</span>{" "}
                  <span className="font-medium">{projectMeta.folderType || "—"}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryNote")}</span>{" "}
                  <span className="font-medium">
                    {noteText.trim() ? t("handover.summaryNoteYes") : t("handover.summaryNoteNo")}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("handover.summaryInstallationDate")}</span>{" "}
                  <span className="font-medium">{installationDate}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div
        className={cn(
          "border-t bg-background p-4 flex gap-2",
          isMobile && "fixed left-0 right-0 z-10 shadow-[0_-2px_8px_rgba(0,0,0,0.15)]",
          isMobile && "bottom-[calc(4rem+env(safe-area-inset-bottom))]"
        )}
      >
        {currentStep > minStep && (
          <Button type="button" variant="outline" onClick={goPrev} className={cn("flex-1", isMobile && "min-h-12 text-base")}>
            <ArrowLeft className={cn("h-4 w-4 mr-2", isMobile && "h-5 w-5")} />
            {t("common.back")}
          </Button>
        )}
        {currentStep < STEPS.SUMMARY && (
          <Button
            type="button"
            onClick={goNext}
            disabled={nextDisabled}
            className={cn("flex-1", isMobile && "min-h-12 text-base")}
          >
            {t("common.next")}
            <ArrowRight className={cn("h-4 w-4 ml-2", isMobile && "h-5 w-5")} />
          </Button>
        )}
        {currentStep === STEPS.SUMMARY && (
          <Button
            type="button"
            onClick={() => void openFinishDialog()}
            disabled={munkainkPhotoCount === 0 || photoUploadBusy}
            className={cn("flex-1", isMobile && "min-h-12 text-base")}
            size="lg"
          >
            {t("handover.finishOpenStage")}
          </Button>
        )}
      </div>

      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent
          className={cn(
            "max-h-[85dvh] overflow-y-auto",
            "pt-[max(1.25rem,calc(0.75rem+env(safe-area-inset-top)))]",
          )}
        >
          <DialogHeader className="space-y-2 pr-10">
            <DialogTitle>{t("handover.stageDialogTitle")}</DialogTitle>
            <DialogDescription>{t("handover.stageDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="handover-installation-date">{t("handover.installationDateLabel")}</Label>
              <DatePickerPopover
                id="handover-installation-date"
                value={installationDate}
                onChange={(v) => setInstallationDate(v || todayYmdLocal())}
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{t("handover.stageSelectLabel")}</p>
              <Select value={selectedStageChoice} onValueChange={setSelectedStageChoice}>
              <SelectTrigger className="w-full" aria-label={t("handover.stageSelectLabel")}>
                <SelectValue placeholder={t("handover.stageSelectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STAGE_VALUE}>{t("handover.stageNoChange")}</SelectItem>
                {stagesInDealSubpipeline.map((s) => {
                  const sid = String(s.id ?? s.stage_id ?? "");
                  if (!sid) return null;
                  return (
                    <SelectItem key={sid} value={sid}>
                      {stageLabel(s)}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setStageDialogOpen(false)} disabled={finishing}>
              {t("common.cancel")}
            </Button>
            <Button type="button" onClick={() => void confirmFinish()} disabled={finishing}>
              {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("handover.stageConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
